<script setup lang="ts">
import { computed, onMounted, reactive, watch } from 'vue';
import ElementLibrarySelector from '@/components/DesignStudio/ElementLibrarySelector.vue';
import ColorOptionsClipboardButtons from '@/components/DesignStudio/ColorOptionsClipboardButtons.vue';
import CSelect, { valueReducer, type CSelectItem } from '@/components/shared/CSelect';
import type { CanvasEx } from '@/typings/canvas';
import type { VectorColor, VectorPlaceholderElement } from '@/typings/customilyElements/canvasElements';
import type { ElementMapOption } from './types';
import type { KeyValueDto } from '@/typings/keyValueDto';
import type { ColorObject } from '@/typings/libraries';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import { useColor } from '@/composables/color';
import { useColorOptionsClipboard } from '@/composables/useColorOptionsClipboard';
import { useToaster } from '@/composables/toaster';
import { DatadogEvent, fireDatadogLog } from '@/utils/datadogUtils';
import { recordFeatureUsage, TrackedFeature } from '@/utils/featureUsage';

const COLOR_TYPE = LibraryTypeEnum.Color;

const vectorTypeOptions: CSelectItem<boolean>[] = [
    {
        label: 'Full Color',
        value: false
    },
    {
        label: 'Single Color',
        value: true
    }
];

const props = defineProps<{
    canvas: CanvasEx;
    vectorElement: VectorPlaceholderElement;
    isSolidBackground?: boolean;
    editMode?: boolean;
    disableColorMode?: boolean;
    readonly?: boolean;
    cmykMode?: boolean;
}>();

const emit = defineEmits<{
    (e: 'open-color-library-selector'): void;
}>();

const model = reactive({
    vectorMode: false,
    colors: [] as VectorColor[],
    colorOption: 0,
    colorMap: new Map<number, ColorObject>(),
    colorLibraryId: undefined as number | undefined
});

const disabled = computed(() => !!props.vectorElement?.locked && !props.isSolidBackground);

watch(
    () => props.vectorElement,
    (element) => {
        if (!element || element.starMapConfig) return;

        model.vectorMode = element.applyColoring || props.isSolidBackground || false;
        model.colorOption = element.selectedColorPosition;
        model.colorLibraryId = element.colorLibraryId;
        initVectorMap(element.colors || []);
    }
);

const initVectorMap = (vectors: VectorColor[]) => {
    model.colors = [...vectors];
    model.colorMap = new Map(model.colors.map((v) => [v.position, v]));
};

const onVectorColorSelected = (color: ElementMapOption, option = 1) => {
    color = color as ColorObject;
    const element = props.vectorElement;
    if (!element) return;

    setEditedProp('colors');

    if (!element.colors) {
        element.colors = [];
    }

    const existingColor = element.colors.find((v) => v.position === option);

    if (existingColor) {
        existingColor.hex = color.hex;
        existingColor.alpha = color.alpha;
        existingColor.cmyk = color.cmyk;
    } else {
        element.colors.push({...color, position: option  });
    }

    initVectorMap(element.colors);
    loadVectorColorIntoElement(option);
};


const previewColorOption = (color?: ElementMapOption) => {
    loadVectorColorIntoElement(model.colorOption || 1, color);
};

const { hexToRgba } = useColor();
const loadVectorColorIntoElement = async (option: number, value?: ElementMapOption) => {
    const element = props.vectorElement;
    const color = (value as ColorObject) || model.colorMap.get(option);

    if (!color) {
        element.selectedColorPosition = model.colorOption = 1;
        return;
    }

    element.selectedColorPosition = model.colorOption = option;
    const rgba = color.alpha < 1 ? hexToRgba(color.hex, color.alpha) : color.hex;
    element.set('svgColor', rgba);
    props.canvas.requestRenderAll();
};

const removeVectorOptionFromMap = (position: number) => {
    const element = props.vectorElement;
    if (!element) return;

    element.colors = element.colors.filter((v) => v.position != position);
    initVectorMap(element.colors);
    setEditedProp('colors');

    loadVectorColorIntoElement(1);
};

const onVectorColorOptionsReordered = (options: KeyValueDto<number, ElementMapOption>[]) => {
    const element = props.vectorElement;
    if (!element) return;

    const colors: VectorColor[] = [];
    let newPosition = 1;
    const userColors = new Map(model.colors.map((v) => [v.position, v]));

    options.forEach(({ id }, index) => {
        const color = userColors.get(id!)!;

        colors.push({ ...color, position: index + 1 });

        if (id === model.colorOption) {
            newPosition = index + 1;
        }
    });

    element.colors = [...colors];

    setEditedProp('colors');
    initVectorMap(colors);
    loadVectorColorIntoElement(newPosition);
};

const pickColorLibrary = () => {
    const element = props.vectorElement;

    element.selectedColorPosition = model.colorOption = 1;
    emit('open-color-library-selector')
}


const removeVectorLibrary = () => {
    const element = props.vectorElement;
    if (!element) return;

    setEditedProp('colorLibraryId');
    element.colorLibraryId = undefined;
    model.colorLibraryId = undefined;
    loadVectorColorIntoElement(1);
};

const updateVectorMode = () => {
    const element = props.vectorElement;
    if (!element) return;

    element.applyColoring = model.vectorMode;
    setEditedProp('applyColoring');

    if (!model.vectorMode) {
        element.selectedColorPosition = model.colorOption = 1;
        removeVectorLibrary();
        element.set('svgColor', undefined);
        props.canvas.requestRenderAll();
        return;
    }

    if (!element.colors?.length) {
        element.colors = [{ position: 1, hex: '#000000', alpha: 1, cmyk: '0,0,0,100' }];
        setEditedProp('colors');
        initVectorMap(element.colors);
    }

    loadVectorColorIntoElement(1);
    model.colorLibraryId = element.colorLibraryId;
};

const setEditedProp = (prop: string) => {
    const element = props.vectorElement;
    if (!element) return;

    if (props.editMode) {
        element.setEditedProp(prop);
    }
};

/* Copy/paste color options, shared with Fill/Stroke/Shadow */
const clipboard = useColorOptionsClipboard();
const toast = useToaster();

const colorSectionLabel = computed(() => (props.isSolidBackground ? 'Solid Bg Color' : 'Vector Color'));

const canCopyPasteVectorColors = computed(() => !disabled.value && !props.readonly);
const canPasteVectorColors = computed(() => {
    const clip = clipboard.clip();
    return canCopyPasteVectorColors.value && !!clip && !clip.hasNonSolidColor;
});

const onCopyVectorColors = () => {
    clipboard.copy({
        colors: new Map(model.colorMap),
        libraryId: model.colorLibraryId
    });
    fireDatadogLog(DatadogEvent.CopyDsColorOptions);
    toast.show(`${colorSectionLabel.value} options copied`);
};

const onPasteVectorColors = () => {
    const clip = clipboard.clip();
    const element = props.vectorElement;
    if (!element || !clip || clip.hasNonSolidColor) return;

    element.colors = [...clip.colors.entries()].map(([position, color]) => ({ ...(color as ColorObject), position }));
    element.colorLibraryId = clip.libraryId;
    setEditedProp('colors');
    setEditedProp('colorLibraryId');
    model.colorLibraryId = clip.libraryId;
    initVectorMap(element.colors);
    loadVectorColorIntoElement(1);
    fireDatadogLog(DatadogEvent.PasteDsColorOptions);
    recordFeatureUsage(TrackedFeature.ColorsPasteVector);
    toast.show(`${colorSectionLabel.value} options pasted`);
};

onMounted(() => {
    model.vectorMode = props?.vectorElement?.applyColoring || !!props.isSolidBackground;
    if (model.vectorMode) {
        if (props.vectorElement.colorLibraryId) {
            model.colorLibraryId = props.vectorElement.colorLibraryId;
        }
        initVectorMap(props.vectorElement.colors);
        loadVectorColorIntoElement(1);
    }
});
</script>

<template>
    <div class="dynamic-vector-properties-wrapper">
        <c-select
            v-if="!props.disableColorMode && !props.isSolidBackground"
            v-model="model.vectorMode"
            class="mb-2"
            :options="vectorTypeOptions"
            label="Vector type"
            :model-transform="valueReducer"
            :disabled="disabled"
            @input="updateVectorMode"
        />
        <section v-if="model.vectorMode || props.isSolidBackground" class="mt-3">
            <color-options-clipboard-buttons
                title="Colors"
                :can-copy="canCopyPasteVectorColors"
                :can-paste="canPasteVectorColors"
                @copy="onCopyVectorColors"
                @paste="onPasteVectorColors"
            />
            <div class="border-bottom mt-2" />
            <element-library-selector
                class="selector-wrapper"
                :disabled="disabled"
                :readonly="readonly"
                :element-list="model.colorMap"
                :library-id="model.colorLibraryId"
                :library-type="COLOR_TYPE"
                :selected-position="model.colorOption"
                :cmyk="cmykMode"
                @option-selected="onVectorColorSelected"
                @load-option="loadVectorColorIntoElement"
                @options-reordered="onVectorColorOptionsReordered"
                @remove-map-option="removeVectorOptionFromMap"
                @remove-library="removeVectorLibrary"
                @pick-library="pickColorLibrary"
                @preview-selection="previewColorOption"
            />
        </section>
    </div>
</template>
